import PageShell from '@/pages/shared/PageShell';
import Button from '@/components/base/Button';

const COLUMNS = [
  { name: 'タスク名', desc: '作業内容を具体的に記載します。「設計書作成」「〇〇画面開発」など、完了したかどうかが明確に判断できる表現にします。', required: true },
  { name: '担当者', desc: 'タスクの責任者を1名記載します。複数人で担当する場合は、主担当を決めて記載しましょう。', required: true },
  { name: '開始日', desc: 'タスクの着手予定日です。YYYY/MM/DDの形式で統一します。', required: true },
  { name: '終了日', desc: 'タスクの完了予定日です。開始日より後の日付を設定します。', required: true },
  { name: '進捗率', desc: '0〜100%で現在の進捗度合いを記載します。週次で更新することをおすすめします。', required: false },
  { name: 'ステータス', desc: '未着手・進行中・完了・保留・中止などから選択します。', required: true },
  { name: '優先度', desc: '高・中・低の3段階でタスクの重要度を設定します。クリティカルパス上のタスクは「高」に設定します。', required: false },
  { name: '依存タスク', desc: 'このタスクを始める前に完了している必要があるタスク名を記載します。', required: false },
  { name: 'マイルストーン', desc: '主要な節目に該当するタスクには「★」などのマークを付けます。', required: false },
  { name: '課題', desc: 'タスクの進行を妨げている問題や懸念事項を記載します。', required: false },
  { name: '成果物', desc: 'タスク完了時に作成される具体的な成果物（ドキュメント名、プログラム名など）を記載します。', required: false },
  { name: '備考', desc: '上記項目に当てはまらない補足情報を自由に記載します。', required: false },
];

export default function TemplatePMExcel() {
  return (
    <PageShell
      title="プロジェクト管理Excelテンプレート無料ダウンロード｜”おもいむすび”"
      description="プロジェクト管理に使えるExcelテンプレートを無料ダウンロード。タスク名、担当者、開始日、終了日、進捗率、ステータス、優先度、依存タスク、マイルストーン、成果物など必須項目を網羅。"
      keywords="プロジェクト管理 エクセル テンプレート,プロジェクト管理 エクセルテンプレート 無料,プロジェクト管理表 テンプレート,プロジェクト管理表 エクセル"
      canonical="https://omoimusubi.asia/templates/project-management-excel/"
      h1="プロジェクト管理Excelテンプレート（無料ダウンロード）"
      subtitle="プロジェクト管理に必要な項目を網羅したExcelテンプレートです。すぐにダウンロードして使い始められます。"
      relatedLinks={[
        { label: 'ガントチャートExcelテンプレート', path: '/templates/gantt-chart-excel/' },
        { label: 'WBSテンプレート', path: '/templates/wbs/' },
        { label: 'Excelと専用ツールの比較', path: '/compare/excel-vs-project-management-tool/' },
        { label: 'プロジェクト管理とは', path: '/guides/project-management/' },
      ]}
    >
      <div className="not-prose my-8 p-8 bg-primary-50 rounded-lg border border-primary-200/70 text-center">
        <div className="w-16 h-16 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
          <i className="ri-download-2-line text-primary-600 text-2xl"></i>
        </div>
        <h2 className="text-xl font-bold text-foreground-900 mb-2">プロジェクト管理表テンプレート</h2>
        <p className="text-sm text-foreground-600 mb-6">以下の12項目を含むExcelテンプレートを無料でダウンロードいただけます。</p>
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {COLUMNS.map((tag) => (
            <span key={tag.name} className="px-3 py-1 bg-background-50 text-xs text-foreground-600 rounded-full border border-background-200">{tag.name}</span>
          ))}
        </div>
        <a href="/templates/project-management-template.xlsx" download className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-base transition-colors cursor-pointer"><i className="ri-download-2-line"></i>無料ダウンロード（Excel）</a><p className="text-xs text-foreground-500 mt-3">会員登録不要・無料でお使いいただけます。</p>
      </div>

      <section>
        <h2>テンプレートの各項目の説明</h2>
        <div className="not-prose overflow-x-auto my-6">
          <table className="min-w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-background-300">
                <th className="text-left p-3 font-bold text-foreground-900 bg-background-100 min-w-[100px]">項目名</th>
                <th className="text-center p-3 font-bold text-foreground-900 bg-background-100 min-w-[60px]">必須</th>
                <th className="text-left p-3 font-bold text-foreground-900 bg-background-100 min-w-[300px]">説明</th>
              </tr>
            </thead>
            <tbody className="text-foreground-600">
              {COLUMNS.map((col) => (
                <tr key={col.name} className="border-b border-background-200/70 hover:bg-background-100/50">
                  <td className="p-3 font-medium text-foreground-800">{col.name}</td>
                  <td className="p-3 text-center">{col.required ? '●' : '○'}</td>
                  <td className="p-3">{col.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2>テンプレートの使い方</h2>
        <ol>
          <li><strong>テンプレートをダウンロード</strong>：上記のボタンからExcelファイルをダウンロードします。</li>
          <li><strong>プロジェクト名とメンバーを入力</strong>：シート上部にプロジェクト名と参加メンバーを記載します。</li>
          <li><strong>タスクを洗い出し</strong>：WBSの考え方を参考に、必要なタスクをすべて列挙します。</li>
          <li><strong>各項目を記入</strong>：タスク名、担当者、開始日、終了日を最低限入力します。</li>
          <li><strong>定期的に更新</strong>：週次で進捗率とステータスを更新し、プロジェクトの状況を最新に保ちます。</li>
          <li><strong>課題があれば即記載</strong>：発生した問題はすぐに課題欄に記入し、対応状況を追跡します。</li>
        </ol>
      </section>

      <section>
        <h2>Excelテンプレートを使う際の注意点</h2>
        <ul>
          <li>ファイルは共有フォルダに保存し、メンバー全員がアクセスできるようにしましょう。</li>
          <li>更新時はファイル名に日付を付与するなど、バージョン管理のルールを決めておきましょう。</li>
          <li>同時編集による競合を避けるため、更新時間をずらすなどの運用ルールが必要です。</li>
          <li>タスク数が50を超える場合は、専用ツールへの移行を検討することをおすすめします。</li>
        </ul>
      </section>

      <section>
        <h2>Excel管理に限界を感じたら</h2>
        <p>テンプレートを使うことでプロジェクト管理の効率は向上しますが、タスク数やメンバー数が増えると手動更新の手間が大きくなります。以下のような状況になったら、専用ツールへの移行を検討しましょう。</p>
        <ul>
          <li>日報で報告された内容を、毎回手動で進捗表に転記している</li>
          <li>「最新のファイルはどれ？」というやり取りが日常的に発生している</li>
          <li>プロジェクトが複数あり、横断的な状況把握ができていない</li>
          <li>海外メンバーと時差のある環境で情報共有に苦労している</li>
        </ul>
        <p>”おもいむすび”なら、日報を入力するだけでAIがタスクの進捗を自動更新し、ガントチャートも自動で最新化されます。まずは無料お試しで、AIによる自動管理を体験してみてください。</p>
      </section>
    </PageShell>
  );
}